/**
 * Paragraph converter — converts ParagraphIR to ProseMirror paragraph/heading nodes.
 *
 * Maps resolved IR properties to TipTap/ProseMirror node attributes.
 * No OOXML knowledge needed here — just unit conversion and attribute mapping.
 */

import type { PMNode } from '../../ooxmlParser/types';
import type { ParagraphIR } from '../irTypes';
import { twipsToPt } from './unitConversion';
import { convertInlinesToPMNodes } from './runConverter';

/**
 * Convert a ParagraphIR to a ProseMirror node.
 *
 * @param para - The paragraph IR node
 * @param generateNodeId - Function to generate unique node IDs
 */
export function convertParagraphIR(
  para: ParagraphIR,
  generateNodeId: () => string
): PMNode {
  const nodeType = para.headingLevel ? 'heading' : 'paragraph';
  const attrs: Record<string, unknown> = {};

  // Node ID
  attrs.id = generateNodeId();

  // Heading level
  if (para.headingLevel) {
    attrs.level = para.headingLevel;
  }

  // Text alignment (map "both" → "justify" for TipTap)
  if (para.alignment) {
    attrs.textAlign = para.alignment === 'both' ? 'justify' : para.alignment;
  }

  // Paragraph style attrs (ParagraphStyleExtension expects "Xpt" strings)
  if (para.leftIndentTwips) {
    const pts = twipsToPt(para.leftIndentTwips);
    attrs.marginLeft = `${pts}pt`;
  }
  if (para.rightIndentTwips) {
    const pts = twipsToPt(para.rightIndentTwips);
    attrs.marginRight = `${pts}pt`;
  }
  if (para.spaceBeforeTwips) {
    const pts = twipsToPt(para.spaceBeforeTwips);
    attrs.marginTop = `${pts}pt`;
  }
  if (para.spaceAfterTwips) {
    const pts = twipsToPt(para.spaceAfterTwips);
    attrs.marginBottom = `${pts}pt`;
  }
  if (para.firstLineIndentTwips) {
    const pts = twipsToPt(para.firstLineIndentTwips);
    attrs.textIndent = `${pts}pt`;
  }
  if (para.lineSpacing) {
    if (para.lineSpacing.rule === 'auto') {
      const multiplier = para.lineSpacing.value / 240;
      if (multiplier !== 1) {
        attrs.lineHeight = String(multiplier);
      }
    }
  }

  // Section break flag (for resolvePageBreaks)
  if (para.hasSectBreak) {
    attrs._hasSectBreak = true;
  }

  // List numbering — store as internal attrs for listConverter to consume
  if (para.list && para.list.numId !== '0') {
    attrs._numId = para.list.numId;
    attrs._numLevel = para.list.level;
  }

  // Convert inline content
  const content = convertInlinesToPMNodes(para.inlines);

  const node: PMNode = { type: nodeType, attrs };
  if (content.length > 0) {
    node.content = content;
  }

  return node;
}
